import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";

import {
  Button,
  Checkbox,
  Input,
  Modal,
  Popover,
  Table,
} from "antd";
import Highlighter from "react-highlight-words";
import { toast } from "react-toastify";

import StyledScrollbar from "../../../Common/UIComponents/StyledScrollbar";
import StyledTooltip from "../../../Common/UIComponents/StyledTooltip";
import CreditPurchaseModal from "../../CreditPurchase/CreditPurchaseModal";

import { getCustomerConfig } from "../../../../utils/common";
import { isPrepaidOrSubscriptionCustomer } from "../../../MyAccount/UsageMetrics/utils";
import {
  buildCreditPurchaseRowsFromClassify,
  getAvailableCreditsByCode,
  getClassifyCreditCheckError,
} from "../../CreditPurchase/utils";
import { isTestNameNotDoableForSide, sampleHasNotDoableTest } from "../../utils";

import { classifyUsageTests, fetchUsageCredits } from "../../../../actions/usage";

import { ReactComponent as FilterLineIcon } from "../../../../assets/icons/Sample Submission Redesign/filter-line.svg";
import { ReactComponent as ListUnorderedIcon } from "../../../../assets/icons/Sample Submission Redesign/list-unordered.svg";
import { ReactComponent as SearchLineIcon } from "../../../../assets/icons/Sample Submission Redesign/search-line.svg";
import { ReactComponent as TestTubeLineIcon } from "../../../../assets/icons/Sample Submission Redesign/test-tube-line.svg";

import "./SampleListViewModal.css";

const formatCell = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  return String(value);
};

const formatTestsSummary = (tests) => {
  if (!tests?.length) return "";
  return tests.join(", ");
};

const sampleTestNames = (sample) => sample.test_list ?? [];

const getSampleTestName = (test) => {
  if (typeof test === "string") return test.trim();
  if (!test || typeof test !== "object") return "";
  return String(test.Test || test.name || test.test_name || test.display_name || "").trim();
};

const toCreditCheckSamples = (samples, sampleCategory) => (
  (Array.isArray(samples) ? samples : []).map((sample) => ({
    sample_id: sample.sample_id || "",
    side: sample.sample_category || sampleCategory || "",
    tests: sampleTestNames(sample).map(getSampleTestName).filter(Boolean),
  }))
);

export default function SampleListViewModal({
  open,
  onCancel,
  sampleList,
  sampleFields,
  sampleFieldsInfo,
  sampleCategory,
  submissionDetails,
  onSelectTestQuantity,
  onSubmitSamples,
  onPayNow,
  creditsResponse = null,
  submitDisabled = false,
  submitTooltip = "",
  testInfoMap,
  creditsLoaded = true,
}) {
  const [viewMode, setViewMode] = useState("list");
  const [searchText, setSearchText] = useState("");
  const [selectedTestName, setSelectedTestName] = useState(null);
  const [selectedColumnKeys, setSelectedColumnKeys] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [creditPurchaseModalOpen, setCreditPurchaseModalOpen] = useState(false);
  const [classifyTestsResponse, setClassifyTestsResponse] = useState(null);
  const [creditsCustomerType, setCreditsCustomerType] = useState(null);
  const [availableCreditsByCode, setAvailableCreditsByCode] = useState(null);
  const creditCheckAbortRef = useRef(null);
  const submitInFlightRef = useRef(false);

  useEffect(() => {
    if (!open) {
      if (creditCheckAbortRef.current) {
        creditCheckAbortRef.current.abort();
      }
      setSearchText("");
      setViewMode("list");
      setSelectedTestName(null);
      setSubmitLoading(false);
    }
  }, [open]);

  useEffect(() => () => {
    if (creditCheckAbortRef.current) {
      creditCheckAbortRef.current.abort();
    }
  }, []);

  const visibleFields = useMemo(
    () => (sampleFields ?? []).filter((f) => {
      if (!sampleFieldsInfo?.[f]) return false;
      if (f === "po" || f === "uuid") return false;
      return true;
    }),
    [sampleFields, sampleFieldsInfo],
  );

  const columnOptions = useMemo(
    () => visibleFields.map((field) => ({
      key: field,
      label: sampleFieldsInfo[field]?.title_field ?? field,
    })),
    [visibleFields, sampleFieldsInfo],
  );

  useEffect(() => {
    setSelectedColumnKeys(visibleFields);
  }, [visibleFields]);

  const highlightSearchWords = useMemo(
    () => searchText.trim().split(/\s+/).filter(Boolean),
    [searchText],
  );

  /** Highlight occurrences of search text in a cell (case-insensitive, multi-keyword). */
  const renderHighlightedCell = useCallback((rawValue) => {
    const text = formatCell(rawValue);
    const inner = !highlightSearchWords.length || text === ""
      ? text
      : (
        <Highlighter
          highlightClassName="searchHighlight"
          searchWords={highlightSearchWords}
          autoEscape
          textToHighlight={text}
        />
      );
    return <span className="SampleListViewModal__cellText">{inner}</span>;
  }, [highlightSearchWords]);

  const filteredSamples = useMemo(() => {
    const list = Array.isArray(sampleList) ? sampleList : [];
    if (!searchText.trim()) return list;
    const words = searchText.trim().toLowerCase().split(/\s+/).filter(Boolean);

    return list.filter((sample) => {
      const testsSummary = formatTestsSummary(sampleTestNames(sample));
      const parts = [
        ...visibleFields.map((f) => sample[f]),
        ...(testsSummary ? [testsSummary] : []),
      ]
        .filter((x) => x !== null && x !== undefined && x !== "")
        .map((x) => String(x).toLowerCase());
      return words.every((w) => parts.some((p) => p.includes(w)));
    });
  }, [sampleList, searchText, visibleFields]);

  const listColumns = useMemo(() => {
    const selectedKeysSet = new Set(selectedColumnKeys);
    const activeFields = visibleFields.filter((field) => selectedKeysSet.has(field));
    const cols = [
      {
        title: "#",
        dataIndex: "index",
        key: "index",
        width: 52,
        render: (idx) => renderHighlightedCell(idx),
      },
    ];
    activeFields.forEach((field) => {
      const title = sampleFieldsInfo[field]?.title_field ?? field;
      const titleLower = String(title).toLowerCase();
      const showSorter = field === "product" || titleLower.includes("product");
      cols.push({
        title,
        dataIndex: field,
        key: field,
        ellipsis: true,
        ...(showSorter ? {
          sorter: (a, b) => String(a[field] ?? "").localeCompare(String(b[field] ?? ""), undefined, { sensitivity: "base" }),
        } : {}),
        render: (text) => renderHighlightedCell(text),
      });
    });
    cols.push({
      title: "Tests",
      dataIndex: "test_list",
      key: "tests",
      ellipsis: true,
      render: (tests) => {
        const summary = formatTestsSummary(tests);
        return renderHighlightedCell(summary);
      },
    });
    return cols;
  }, [visibleFields, sampleFieldsInfo, selectedColumnKeys, renderHighlightedCell]);

  const testsRequested = useMemo(() => {
    const counts = new Map();
    filteredSamples.forEach((sample) => {
      sampleTestNames(sample).forEach((testName) => {
        counts.set(testName, (counts.get(testName) || 0) + 1);
      });
    });
    const list = Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

    const words = searchText.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return list;

    const narrowed = list.filter(({ name }) => {
      const nl = name.toLowerCase();
      return words.every((w) => nl.includes(w));
    });
    return narrowed.length > 0 ? narrowed : list;
  }, [filteredSamples, searchText]);

  const selectedTestEffective = useMemo(() => {
    if (!testsRequested.length) return null;
    if (selectedTestName && testsRequested.some((t) => t.name === selectedTestName)) {
      return selectedTestName;
    }
    return testsRequested[0].name;
  }, [testsRequested, selectedTestName]);

  useEffect(() => {
    if (!selectedTestName) return;
    if (!testsRequested.some((t) => t.name === selectedTestName)) {
      setSelectedTestName(null);
    }
  }, [testsRequested, selectedTestName]);

  const linkedSamplesForTest = useMemo(() => {
    if (!selectedTestEffective) return [];
    return filteredSamples.filter((s) => sampleTestNames(s).includes(selectedTestEffective));
  }, [filteredSamples, selectedTestEffective]);

  const listDataSource = useMemo(
    () => filteredSamples.map((sample) => {
      const originalIdx = (Array.isArray(sampleList) ? sampleList : []).indexOf(sample);
      const row = {
        key: sample.uuid,
        index: originalIdx + 1,
        sample_id: sample.sample_id,
        side: sample.sample_category || sampleCategory,
        test_list: sample.test_list,
        notDoable: sampleHasNotDoableTest(sample, testInfoMap, sampleCategory),
      };
      visibleFields.forEach((f) => {
        row[f] = sample[f];
      });
      return row;
    }),
    [filteredSamples, sampleList, visibleFields, sampleCategory, testInfoMap],
  );

  const handleSubmitSamples = async () => {
    if (submitInFlightRef.current || submitLoading || submitDisabled) return;

    submitInFlightRef.current = true;
    setSubmitLoading(true);

    try {
      const customerType = creditsResponse?.customer_type ?? getCustomerConfig()?.customer_type;
      if (!isPrepaidOrSubscriptionCustomer(customerType)) {
        onSubmitSamples();
        return;
      }

      if (!creditsLoaded) {
        toast.error("Unable to load remaining credits.");
        return;
      }

      if (creditCheckAbortRef.current) {
        creditCheckAbortRef.current.abort();
      }
      const controller = new AbortController();
      creditCheckAbortRef.current = controller;

      const samples = toCreditCheckSamples(sampleList, sampleCategory);

      const [classifyResponse, latestCreditsResponse] = await Promise.all([
        classifyUsageTests({ samples, signal: controller.signal }),
        fetchUsageCredits({ allLocation: true, signal: controller.signal }),
      ]);

      if (controller.signal.aborted || classifyResponse.canceled || latestCreditsResponse.canceled) {
        return;
      }

      if (!classifyResponse.success) {
        toast.error(classifyResponse.message || "Unable to classify tests.");
        return;
      }

      if (!latestCreditsResponse?.success) {
        toast.error(latestCreditsResponse?.message || "Unable to load remaining credits.");
        return;
      }

      const classifyError = getClassifyCreditCheckError(classifyResponse, samples);
      if (classifyError) {
        toast.error(classifyError);
        return;
      }

      const remainingCreditsByCode = getAvailableCreditsByCode(latestCreditsResponse);
      const creditsRequiredRows = buildCreditPurchaseRowsFromClassify(classifyResponse, {
        availableCreditsByCode: remainingCreditsByCode,
      });

      if (!creditsRequiredRows) {
        onSubmitSamples();
        return;
      }

      setClassifyTestsResponse(classifyResponse);
      setCreditsCustomerType(latestCreditsResponse.customer_type || null);
      setAvailableCreditsByCode(remainingCreditsByCode);
      setCreditPurchaseModalOpen(true);
    } finally {
      submitInFlightRef.current = false;
      setSubmitLoading(false);
    }
  };

  const modalTitle = (submissionDetails?.submissionName || "").trim();

  return (
    <>
      <Modal
        open={open}
        onCancel={onCancel}
        title={modalTitle}
        width={1180}
        className="SampleListViewModal"
        wrapClassName="SampleListViewModal__wrap"
        footer={(
          <div className="SampleListViewModal__footer">
            <Button type="default" htmlType="button" className="Ant__CancelBtn SampleListViewModal__footerCancelBtn" onClick={onCancel}>
              Cancel
            </Button>
            <StyledTooltip title={submitDisabled ? submitTooltip : ""} placement="top">
              <span className="SampleListViewModal__footerSubmitWrap">
                <Button
                  type="primary"
                  htmlType="button"
                  className="Ant__SubmitBtn SampleListViewModal__footerSubmit"
                  onClick={handleSubmitSamples}
                  loading={submitLoading}
                  disabled={submitLoading || submitDisabled}
                >
                  Submit Samples
                </Button>
              </span>
            </StyledTooltip>
          </div>
        )}
        destroyOnClose
      >
        <div className={`SampleListViewModal__toolbar ${viewMode === "tests" ? "SampleListViewModal__toolbar--testView" : ""}`}>
          <div className="SampleListViewModal__toggleGroup" role="tablist" aria-label="View mode">
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === "list"}
              className={`SampleListViewModal__toggle ${viewMode === "list" ? "SampleListViewModal__toggle--active" : ""}`}
              onClick={() => setViewMode("list")}
            >
              <ListUnorderedIcon aria-hidden className="SampleListViewModal__toggleSvg" />
              <span>List View</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === "tests"}
              className={`SampleListViewModal__toggle ${viewMode === "tests" ? "SampleListViewModal__toggle--active" : ""}`}
              onClick={() => setViewMode("tests")}
            >
              <TestTubeLineIcon aria-hidden className="SampleListViewModal__toggleSvg" />
              <span>Test View</span>
            </button>
          </div>
          <Input
            className="SampleListViewModal__search"
            allowClear
            placeholder="Search anything..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            prefix={<SearchLineIcon aria-hidden className="SampleListViewModal__searchIcon" />}
          />
          {viewMode === "list" && (
            <Popover
              trigger="click"
              placement="bottomRight"
              overlayClassName="SampleListViewModal__columnsPopover"
              content={(
                <div className="SampleListViewModal__columnsPopoverBody">
                  <Checkbox.Group
                    value={selectedColumnKeys}
                    onChange={(vals) => setSelectedColumnKeys(vals)}
                    className="SampleListViewModal__columnsCheckboxGroup"
                  >
                    {columnOptions.map((option) => (
                      <label key={option.key} className="SampleListViewModal__columnsCheckboxRow">
                        <Checkbox value={option.key}>{option.label}</Checkbox>
                      </label>
                    ))}
                  </Checkbox.Group>
                </div>
              )}
            >
              <Button type="default" className="SampleListViewModal__columnsBtn">
                <FilterLineIcon aria-hidden className="SampleListViewModal__columnsIcon" />
                <span>Columns</span>
              </Button>
            </Popover>
          )}
        </div>
        <div className="SampleListViewModal__tableWrap">
          <StyledScrollbar
            className={`SampleListViewModal__tableScrollbar${viewMode === "tests" ? " SampleListViewModal__tableScrollbar--testView" : ""}`}
          >
            {viewMode === "list" ? (
              <Table
                className="SampleListViewModal__table"
                size="small"
                pagination={false}
                columns={listColumns}
                dataSource={listDataSource}
                rowClassName={(record) => (record.notDoable ? "SampleListViewModal__row--notDoable" : "")}
              />
            ) : (
              <div className="SampleListViewModal__testViewBody">
                <aside className="SampleListViewModal__testViewSidebar">
                  <div className="SampleListViewModal__testViewSidebarTitle">Tests Requested</div>
                  {testsRequested.length === 0 ? (
                    <div className="SampleListViewModal__testViewEmpty">No tests match the current search.</div>
                  ) : (
                    <StyledScrollbar noScrollX className="SampleListViewModal__testViewSidebarScrollbar">
                      <ul className="SampleListViewModal__testViewNavList">
                        {testsRequested.map(({ name, count }) => (
                          <li key={name}>
                            <Button
                              type="text"
                              htmlType="button"
                              className={`SampleListViewModal__testViewNavButton ${name === selectedTestEffective ? "SampleListViewModal__testViewNavButton--active" : ""}${isTestNameNotDoableForSide(name, testInfoMap, sampleCategory) ? " SampleListViewModal__testViewNavButton--notDoable" : ""}`}
                              onClick={() => setSelectedTestName(name)}
                            >
                              {!highlightSearchWords.length ? (
                                `${name} (${count})`
                              ) : (
                                <>
                                  <Highlighter
                                    highlightClassName="searchHighlight"
                                    searchWords={highlightSearchWords}
                                    autoEscape
                                    textToHighlight={name}
                                  />
                                  {` (${count})`}
                                </>
                              )}
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </StyledScrollbar>
                  )}
                </aside>
                <div className="SampleListViewModal__testViewMain">
                  <div className="SampleListViewModal__testViewMainTitle">Sample Linked to Test</div>
                  {linkedSamplesForTest.length === 0 ? (
                    <div className="SampleListViewModal__testViewEmpty">No samples include this test.</div>
                  ) : (
                    <StyledScrollbar noScrollX className="SampleListViewModal__testViewMainScrollbar">
                      <ul className="SampleListViewModal__linkedSampleList">
                        {linkedSamplesForTest.map((sample) => {
                          const n = (Array.isArray(sampleList) ? sampleList : []).indexOf(sample) + 1;
                          const line = sample.sample_id?.trim() || "Untitled sample";
                          return (
                            <li key={sample.uuid} className={`SampleListViewModal__linkedSampleItem${sampleHasNotDoableTest(sample, testInfoMap, sampleCategory) ? " SampleListViewModal__linkedSampleItem--notDoable" : ""}`}>
                              <span className="SampleListViewModal__linkedSampleIndex">{`${n}.`}</span>
                              <span className="SampleListViewModal__linkedSampleText">
                                {!highlightSearchWords.length ? line : (
                                  <Highlighter
                                    highlightClassName="searchHighlight"
                                    searchWords={highlightSearchWords}
                                    autoEscape
                                    textToHighlight={line}
                                  />
                                )}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </StyledScrollbar>
                  )}
                </div>
              </div>
            )}
          </StyledScrollbar>
        </div>
      </Modal>
      <CreditPurchaseModal
        open={creditPurchaseModalOpen}
        onClose={() => {
          setCreditPurchaseModalOpen(false);
          setClassifyTestsResponse(null);
          setCreditsCustomerType(null);
          setAvailableCreditsByCode(null);
        }}
        classifyResponse={classifyTestsResponse}
        availableCreditsByCode={availableCreditsByCode}
        variant={creditsCustomerType}
        onSelectTestQuantity={onSelectTestQuantity}
        onPayNow={onPayNow || onSubmitSamples}
      />
    </>
  );
}
